import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { RiArrowDownSLine } from 'react-icons/ri';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from 'components/backButton/BackButton';
import CommonInput from 'components/commonInput/CommonInput';
import CommonSubmitBtn from 'components/commonInput/CommonSubmitBtn';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import PageLoader from 'components/wholePageLoader/PageLoader';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import LabelBadge from 'components/labels/LabelBadge';
import LabelScopeModal from 'components/labels/LabelScopeModal';
import { useDebounce } from 'hooks/useDebounce';
import useRegions from 'hooks/useRegions';
import {
  changeLabelScope,
  checkNameConflict,
  createLabel,
  getLabelById,
  updateLabel,
} from 'utils/labelApi';
import { showErrorAlert, showSuccessAlert } from 'utils/AlertUtils';
import { labelAddEditValidation } from 'utils/CommonValidation';
import {
  GLOBAL_LABEL_SCOPE_REGION_ID,
  labelColorOptions,
  sideBarRoutes,
} from 'utils/StaticData';

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

// Full page for create/edit (RAT-1687/1699) - matches the app's other
// AddEdit pages instead of a modal, so navigation and layout stay consistent.
// In edit mode, picking a different Scope (Super Admin only) runs the same
// conflict-merge/keep-both and downgrade-impact-preview checks as the list's
// "Change scope" flow, but only *stages* the result - nothing is written to
// the server until the page's own Save is clicked, same as renaming or
// recoloring.
const LabelAddEdit = () => {
  const { editLabelId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editLabelId);
  const { textColor, brandStars } = useColorModeVariables();
  const { regions } = useRegions();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const isSuperAdmin = adminDetails?.role === sideBarRoutes?.Admin;
  const ownRegionId = adminDetails?.region?.regionId;

  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [color, setColor] = useState(labelColorOptions[0]);
  const [customHex, setCustomHex] = useState('');
  const [nameConflict, setNameConflict] = useState(null);
  const [isCheckingName, setIsCheckingName] = useState(false);
  // The label's real, currently-saved scope - separate from the form field,
  // which may show an unsaved pending selection while editing.
  const [originalRegionId, setOriginalRegionId] = useState(null);
  const [originalRegionLabel, setOriginalRegionLabel] = useState('Global');
  // { targetRegionId, resolution, label } once a scope change has been
  // confirmed through the modal below - applied only on final Save.
  const [pendingScope, setPendingScope] = useState(null);
  const pendingTargetRef = useRef(null);

  // Direct region-to-region moves aren't a supported transition (the backend
  // only does region<->Global) - while editing a label that's already
  // scoped to a specific region, only offer Global as an alternative rather
  // than let the user pick another region and hit an error. A second edit
  // (Global -> the real target) covers the rest.
  const isEditingSpecificRegion =
    isEdit &&
    originalRegionId !== null &&
    originalRegionId !== GLOBAL_LABEL_SCOPE_REGION_ID;

  const scopeOptions = isSuperAdmin
    ? isEditingSpecificRegion
      ? [
          { id: GLOBAL_LABEL_SCOPE_REGION_ID, label: 'Global' },
          ...regions
            .filter((r) => r.id === originalRegionId)
            .map((r) => ({ id: r.id, label: r.label })),
        ]
      : [
          { id: GLOBAL_LABEL_SCOPE_REGION_ID, label: 'Global' },
          ...regions.map((r) => ({ id: r.id, label: r.label })),
        ]
    : regions
        .filter((r) => r.id === ownRegionId)
        .map((r) => ({ id: r.id, label: r.label }));

  const {
    handleSubmit,
    setValue,
    watch,
    register,
    formState: { errors },
    control,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(labelAddEditValidation),
    defaultValues: {
      name: '',
      regionId: isSuperAdmin
        ? { id: GLOBAL_LABEL_SCOPE_REGION_ID, label: 'Global' }
        : null,
    },
  });

  const name = watch('name') || '';
  const scopeValue = watch('regionId');
  const scopeRegionId = scopeValue?.id;
  const debouncedName = useDebounce(name, 400);

  // Dealer/Seller have exactly one legal scope (their own region) and the
  // field is disabled, so it must be pre-selected for them - `regions` loads
  // async, so this can't just be a useForm defaultValue.
  useEffect(() => {
    if (isEdit || isSuperAdmin || !ownRegionId) return;
    const ownRegion = regions.find((r) => r.id === ownRegionId);
    if (ownRegion) {
      setValue('regionId', { id: ownRegion.id, label: ownRegion.label });
    }
  }, [isEdit, isSuperAdmin, ownRegionId, regions, setValue]);

  useEffect(() => {
    if (!isEdit) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getLabelById(editLabelId)
      .then((data) => {
        if (cancelled) return;
        setValue('name', data?.name || '');
        const regionId = data?.regionId ?? GLOBAL_LABEL_SCOPE_REGION_ID;
        const regionLabel = data?.regionName || 'Global';
        setValue('regionId', { id: regionId, label: regionLabel });
        setOriginalRegionId(regionId);
        setOriginalRegionLabel(regionLabel);
        setColor(data?.color || labelColorOptions[0]);
        setCustomHex(
          data?.color && !labelColorOptions.includes(data.color)
            ? data.color
            : '',
        );
      })
      .catch((error) => {
        showErrorAlert(
          error?.message || error?.error?.message || 'Failed to load label',
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, editLabelId, setValue]);

  // Live per-scope name-uniqueness check (RAT-1687), same as before it lived
  // in a modal - unrelated to yup, which only validates synchronously.
  useEffect(() => {
    const effectiveRegionId = isEdit ? originalRegionId : scopeRegionId;
    if (
      !name.trim() ||
      effectiveRegionId === undefined ||
      effectiveRegionId === null
    ) {
      setNameConflict(null);
      setIsCheckingName(false);
      return;
    }
    if (debouncedName !== name) {
      setIsCheckingName(true);
      return;
    }
    let cancelled = false;
    setIsCheckingName(true);
    // In edit mode, a rename is validated server-side against the label's
    // real (original) scope - any pending-but-unsaved scope change hasn't
    // happened yet, so checking against it here would be misleading.
    checkNameConflict(
      debouncedName,
      isEdit ? originalRegionId : scopeRegionId,
      editLabelId,
    )
      .then((result) => {
        if (!cancelled) setNameConflict(result);
      })
      .finally(() => {
        if (!cancelled) setIsCheckingName(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    name,
    debouncedName,
    scopeRegionId,
    editLabelId,
    isEdit,
    originalRegionId,
  ]);

  const isCustomHexValid = HEX_PATTERN.test(customHex);
  const effectiveColor = isCustomHexValid ? customHex : color;
  // Only flag it once they've typed the full length (matches the input's
  // maxLength) - erroring on the first keystroke was flagging a hex the user
  // hadn't finished entering yet.
  const hexInvalid = customHex.length === 7 && !isCustomHexValid;
  const colorPicker = useDisclosure();
  const scopeModal = useDisclosure();

  // The modal always evaluates against the label's real (original) scope -
  // nothing has actually changed server-side yet, no matter what's staged.
  const scopeModalLabel = {
    id: editLabelId,
    name,
    regionId: originalRegionId,
    color: effectiveColor,
  };

  // Picking a new Scope in edit mode never commits directly - it runs the
  // same conflict/impact checks as the list (via the modal below) and, once
  // confirmed, only stages the result for the page's own Save.
  const handleScopeChange = (selected, formOnChange) => {
    if (!isEdit) {
      formOnChange(selected);
      return;
    }
    if (!selected || selected.id === scopeRegionId) return;
    if (selected.id === originalRegionId) {
      // Back to what's actually saved - drop the pending change, no checks
      // needed since this isn't really a change.
      setPendingScope(null);
      formOnChange(selected);
      return;
    }
    const isOriginalGlobal = originalRegionId === GLOBAL_LABEL_SCOPE_REGION_ID;
    const isTargetGlobal = selected.id === GLOBAL_LABEL_SCOPE_REGION_ID;
    if (!isOriginalGlobal && !isTargetGlobal) {
      showErrorAlert(
        "Can't move directly between two regions - convert to Global and save, then downgrade to the target region in a separate edit.",
      );
      return;
    }
    pendingTargetRef.current = selected;
    scopeModal.onOpen();
  };

  const handleScopeConfirm = (payload) => {
    const targetLabel =
      payload.targetRegionId === GLOBAL_LABEL_SCOPE_REGION_ID
        ? 'Global'
        : regions.find((r) => r.id === payload.targetRegionId)?.label || '';
    setPendingScope(payload);
    setValue('regionId', { id: payload.targetRegionId, label: targetLabel });
    scopeModal.onClose();
  };

  const onSubmit = async (data) => {
    if (nameConflict || hexInvalid || isCheckingName) return;
    setIsSaving(true);
    try {
      if (isEdit) {
        await updateLabel(editLabelId, {
          name: data.name.trim(),
          color: effectiveColor,
        });
        if (pendingScope) {
          // A merge resolution deletes this label and moves its assignments
          // onto the existing Global one - the id we just updated may no
          // longer exist afterward, which is fine since we navigate away
          // either way.
          await changeLabelScope(editLabelId, pendingScope);
        }
        showSuccessAlert('Label updated');
      } else {
        await createLabel({
          name: data.name.trim(),
          regionId: data.regionId?.id,
          color: effectiveColor,
        });
        showSuccessAlert('Label created');
      }
      navigate('/admin/labels');
    } catch (error) {
      showErrorAlert(
        error?.message || error?.error?.message || 'Failed to save label',
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex mb={4}>
        <BackButton fallbackUrl="/admin/labels" label="Back to Labels" />
      </Flex>

      <Flex direction="column" align="center" justify="center">
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="50px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Text fontSize="2xl" fontWeight="bold" mb={6}>
            {isEdit ? 'Edit Label' : 'Create New Label'}
          </Text>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormControl>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
                <Box>
                  <CommonInput
                    type="text"
                    name="name"
                    labelText="Label Name"
                    textColor={textColor}
                    brandStars={brandStars}
                    placeholder="e.g. Tier 1 Partner"
                    register={register('name')}
                    errors={errors?.name}
                  />
                  {isCheckingName && (
                    <Spinner size="sm" color="reviveOrange" mt="2" />
                  )}
                  {nameConflict && (
                    <Alert status="error" borderRadius="10px" mt="2" py="2">
                      <AlertIcon />
                      <Text fontSize="0.8rem">
                        A label named &quot;{nameConflict.name}&quot; already
                        exists in {nameConflict.regionName}. Names must be
                        unique within a scope.
                      </Text>
                    </Alert>
                  )}
                </Box>

                <Box>
                  <Controller
                    name="regionId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="Scope"
                        name="regionId"
                        type="select"
                        options={scopeOptions}
                        value={value}
                        onChange={(selected) =>
                          handleScopeChange(selected, onChange)
                        }
                        register={register}
                        isRequired
                        error={errors?.regionId}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                        isDisabled={!isSuperAdmin}
                      />
                    )}
                  />
                  {isEdit && pendingScope && (
                    <Text fontSize="0.75rem" color="reviveOrange" mt="2">
                      Scope will change to{' '}
                      {pendingScope.targetRegionId ===
                      GLOBAL_LABEL_SCOPE_REGION_ID
                        ? 'Global'
                        : regions.find(
                            (r) => r.id === pendingScope.targetRegionId,
                          )?.label}{' '}
                      when you save.
                    </Text>
                  )}
                  {isEdit && isSuperAdmin && !pendingScope && (
                    <Text fontSize="0.7rem" color="gray.500" mt="2">
                      Currently {originalRegionLabel} - pick a different scope
                      to change it (applies on save).
                    </Text>
                  )}
                </Box>
              </SimpleGrid>

              <Flex mt="24px" align="flex-start" gap="32px" wrap="wrap">
                <Box>
                  <FormLabel fontSize="sm" fontWeight="600" color="primary">
                    Color
                  </FormLabel>
                  <Popover
                    isOpen={colorPicker.isOpen}
                    onOpen={colorPicker.onOpen}
                    onClose={colorPicker.onClose}
                    placement="bottom-start"
                    gutter={8}
                    modifiers={[{ name: 'flip', enabled: false }]}
                  >
                    <PopoverTrigger>
                      <Button
                        type="button"
                        variant="outline"
                        borderColor="#d8dadc"
                        background="#fff"
                        px="10px"
                        h="auto"
                        py="6px"
                        rightIcon={
                          <Icon as={RiArrowDownSLine} color="gray.500" />
                        }
                      >
                        <Box
                          w="24px"
                          h="24px"
                          borderRadius="6px"
                          bg={effectiveColor}
                          border="1px solid"
                          borderColor="blackAlpha.200"
                          flexShrink={0}
                        />
                      </Button>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent
                        w="fit-content"
                        borderRadius="12px"
                        zIndex="popover"
                      >
                        <PopoverBody p="12px">
                          <SimpleGrid
                            columns={10}
                            spacing="6px"
                            mb="10px"
                            width="fit-content"
                          >
                            {labelColorOptions.map((c) => {
                              const isSelected = !customHex && color === c;
                              return (
                                <Tooltip
                                  key={c}
                                  label={c}
                                  fontSize="xs"
                                  openDelay={300}
                                >
                                  <Box
                                    as="button"
                                    type="button"
                                    aria-label={`Select color ${c}`}
                                    onClick={() => {
                                      setColor(c);
                                      setCustomHex('');
                                      colorPicker.onClose();
                                    }}
                                    w="20px"
                                    h="20px"
                                    borderRadius="4px"
                                    bg={c}
                                    cursor="pointer"
                                    border="none"
                                    boxShadow={
                                      isSelected
                                        ? '0 0 0 2px #fff, 0 0 0 4px var(--chakra-colors-primary)'
                                        : 'none'
                                    }
                                  />
                                </Tooltip>
                              );
                            })}
                          </SimpleGrid>
                          <Text
                            fontSize="0.65rem"
                            color="gray.500"
                            mb="6px"
                            fontWeight="600"
                            textTransform="uppercase"
                            letterSpacing="0.04em"
                          >
                            Custom
                          </Text>
                          <InputGroup size="sm" w="220px">
                            <Input
                              placeholder="#1A2B3C"
                              value={customHex}
                              onChange={(e) => setCustomHex(e.target.value)}
                              background="#fff"
                              border="1px solid #d8dadc"
                              borderRadius="8px"
                              isInvalid={hexInvalid}
                              maxLength={7}
                              pr="2.5rem"
                            />
                            <InputRightElement
                              width="2.5rem"
                              justifyContent="center"
                            >
                              <Box
                                w="18px"
                                h="18px"
                                borderRadius="4px"
                                bg={
                                  isCustomHexValid ? customHex : 'transparent'
                                }
                                border={
                                  isCustomHexValid ? '1px solid' : '1px dashed'
                                }
                                borderColor={
                                  isCustomHexValid
                                    ? 'blackAlpha.200'
                                    : 'gray.300'
                                }
                              />
                            </InputRightElement>
                          </InputGroup>
                          <Text fontSize="0.65rem" color="gray.400" mt="1">
                            Click outside to apply.
                          </Text>
                          {hexInvalid && (
                            <Text fontSize="0.7rem" color="red.500" mt="1">
                              Enter a 6-digit hex, e.g. #1A2B3C.
                            </Text>
                          )}
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </Popover>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="600" color="primary" mb="8px">
                    Preview
                  </Text>
                  <Flex align="center" h="38px">
                    <LabelBadge
                      label={{
                        name: name || 'Label name',
                        color: effectiveColor,
                      }}
                    />
                  </Flex>
                </Box>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
                <CommonSubmitBtn
                  BTNtext={isEdit ? 'Save' : 'Create Label'}
                  isLoading={isSaving}
                />
              </SimpleGrid>
            </FormControl>
          </form>
        </Card>
      </Flex>

      {isEdit && (
        <LabelScopeModal
          isOpen={scopeModal.isOpen}
          onClose={scopeModal.onClose}
          label={scopeModalLabel}
          onConfirm={handleScopeConfirm}
          initialTargetRegionId={pendingTargetRef.current?.id}
        />
      )}
    </Box>
  );
};

export default LabelAddEdit;
